<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { useProjectWrite } from '@/utils/project-write';
import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import ProjectClosurePanel from '@/components/ProjectClosurePanel.vue';
import ProjectReadinessCard from '@/components/ProjectReadinessCard.vue';
import ExportDataButton from '@/components/ExportDataButton.vue';
import MilestoneRecoveryForm from '@/components/MilestoneRecoveryForm.vue';
import type { RecoverableMilestone } from '@/utils/milestone-recovery';
import StatusChip from '@/components/StatusChip.vue';
import { notify } from '@/services/notice';
import type { PlanSummary, PortfolioResponse, Project, ProjectDetail } from '@/types/domain';
import { buildExportFilename } from '@/utils/csv-export';
import { errorMessage, formatDate } from '@/utils/format';
import { milestoneStatusLabel } from '@/utils/gates';
import { MILESTONE_AUDIENCE_HINT, MILESTONE_AUDIENCE_LABEL, milestoneMeta, milestoneOrder } from '@/utils/milestones';

const props = defineProps<{ project: Project; summary?: PlanSummary; portfolio: PortfolioResponse; detail: ProjectDetail }>();
const { access } = useSession();
/*
  能不能改 = 账号不是只读 且 这个项目在自己名下。
  所有项目都能看（Tony 2026-08-18 定的口径），收着的只有写。
*/
const canWrite = useProjectWrite(computed(() => props.project));
/*
  移交量产（0828）：项目经理/管理员发起，项目总监签字。
  两个角色分开传——发起和拍板不该是同一个人，界面上也别让人以为自己两件都能做。
*/
const isDirector = computed(() => (access.value?.roles ?? []).some(
  (role) => role === 'director' || role === 'admin'));

/**
 * 本地副本：里程碑数据由父组件从项目详情带进来，
 * 行内改完不值得为一个日期整页重载，改本地这份就是改完后的事实。
 */
type MilestoneRow = ProjectDetail['milestones'][number];
const localMilestones = ref<MilestoneRow[]>([]);
watch(() => props.detail.milestones, (list) => { localMilestones.value = list.map((item) => ({ ...item })); }, { immediate: true });
/*
  按**标准流程顺序**排，不按计划日期（0820 P9）。
  迁移项目的日期是乱的，按日期排会把「项目启动」排到「PPAP 提交」后面。
  同序号（认不出的代号）之间再按日期兜底，保证顺序稳定。
*/
const milestones = computed(() => [...localMilestones.value].sort((a, b) => {
  const delta = milestoneOrder(a.milestoneCode) - milestoneOrder(b.milestoneCode);
  if (delta !== 0) return delta;
  if (!a.planDate) return 1;
  if (!b.planDate) return -1;
  return a.planDate.localeCompare(b.planDate);
}));

/**
 * 分成「客户里程碑」和「内部里程碑」两组。
 *
 * 原来 13 个代号平铺一列，GATE1/I_OTS/LAUNCH 这种代号看不出哪个是
 * 对客户承诺的日子（试用反馈 0814）。客户组排前面——那几个误期要对外解释。
 */
const milestoneGroups = computed(() => (['customer', 'internal'] as const)
  .map((audience) => ({
    audience,
    label: MILESTONE_AUDIENCE_LABEL[audience],
    hint: MILESTONE_AUDIENCE_HINT[audience],
    items: milestones.value.filter((item) => milestoneMeta(item.milestoneCode).audience === audience),
  }))
  .filter((group) => group.items.length));

function metaOf(code: string | null | undefined) {
  return milestoneMeta(code);
}

const savingMilestoneId = ref<string | number | null>(null);

/** 上海时区的今天；实际日期的上限 */
const today = computed(() =>
  new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10));

async function saveMilestone(
  milestone: MilestoneRow,
  field: 'planDate' | 'actualDate' | 'remark',
  event: Event,
): Promise<void> {
  const input = event.target as HTMLInputElement;
  const raw = input.value.trim();
  const value = raw || null;
  if ((milestone[field] ?? null) === value) return;
  // 实际日期不能是未来：这里先拦一次给即时反馈，后端还会再拦一次
  if (field === 'actualDate' && value && value.slice(0, 10) > today.value) {
    input.value = (milestone.actualDate ?? '').slice(0, 10);
    notify(
      'error',
      `里程碑「${milestone.milestoneCode}」未保存`,
      `实际日期不能填未来的日期（今天是 ${today.value}）。还没完成就先留空，要表达计划请改「计划」那一格。`,
    );
    return;
  }
  const previous = milestone[field] ?? null;
  milestone[field] = value;
  savingMilestoneId.value = milestone.id;
  try {
    await eqsApi.updateMilestone(props.project.id, milestone.id, { [field]: value });
  } catch (caught) {
    milestone[field] = previous;
    notify('error', `里程碑「${milestone.milestoneCode}」未保存`, errorMessage(caught));
  } finally {
    savingMilestoneId.value = null;
  }
}
/**
 * 挽回计划登记完（0923）：把返回的那几格并进本地这一行，不整页重载。
 * 项目灯在组合页 / 页头那份快照里，父级下次取数时自然跟上。
 */
function applyRecovery(milestone: MilestoneRow, saved: RecoverableMilestone): void {
  milestone.recoveryDate = saved.recoveryDate ?? null;
  milestone.recoveryNote = saved.recoveryNote ?? null;
  milestone.recoveryByName = saved.recoveryByName ?? null;
  milestone.recoveryAt = saved.recoveryAt ?? null;
  milestone.recoveryCount = saved.recoveryCount ?? null;
}

const exportColumns = [
  { key: 'category', label: '分类' },
  { key: 'item', label: '项目' },
  { key: 'value', label: '当前值' },
  { key: 'planDate', label: '计划日期' },
  { key: 'actualDate', label: '实际日期' },
  { key: 'status', label: '状态' },
  { key: 'remark', label: '说明' },
];
const exportRows = computed(() => [
  { category: '项目资料', item: '项目编号', value: props.project.projectNo },
  { category: '项目资料', item: '项目名称', value: props.project.projectName },
  { category: '项目资料', item: '客户', value: props.project.customer },
  { category: '项目资料', item: '项目经理', value: props.project.projectManager },
  { category: '项目资料', item: '项目类型', value: props.project.projectType },
  { category: '项目资料', item: '优先级', value: props.project.priority },
  { category: '项目资料', item: '项目周期', planDate: props.project.startDate, actualDate: props.project.endDate },
  { category: '项目统计', item: '阶段', value: props.summary?.phaseCount },
  { category: '项目统计', item: '任务', value: props.summary?.taskCount },
  { category: '项目统计', item: '计划节点', value: props.summary?.plannedNodes },
  { category: '项目统计', item: '完成节点', value: props.summary?.completedNodes },
  { category: '项目统计', item: '关键交付物', value: props.summary?.keyDeliverables },
  { category: '项目统计', item: '节点总数', value: props.summary?.nodeCount },
  ...milestones.value.map((milestone) => ({
    category: '里程碑 / Gate',
    item: milestone.milestoneCode,
    value: '',
    planDate: milestone.planDate,
    actualDate: milestone.actualDate,
    status: milestone.gateStatus || (milestone.actualDate ? 'completed' : 'pending'),
    remark: milestone.remark,
  })),
]);
const exportFilename = computed(() =>
  buildExportFilename([props.project.projectNo, '项目概览']));
</script>

<template>
  <section class="workspace-grid">
    <!-- 缺口清单放在最前：打开项目第一眼看到的应该是「该补什么」，不是已有数据的统计 -->
    <ProjectReadinessCard class="readiness-span" :project-id="project.id" />

    <!-- 结项：概览页最下面单独一块，不和日常指标混在一起（0828 Tony 要「仪式感」） -->
    <ProjectClosurePanel
      class="readiness-span"
      :project-id="project.id"
      :can-manage="canWrite"
      :is-director="isDirector"
    />

    <article class="content-panel project-snapshot">
      <header class="section-heading">
        <div><span>PROJECT SNAPSHOT</span><h2>项目数据快照</h2></div>
        <ExportDataButton :columns="exportColumns" :rows="exportRows" :filename="exportFilename" label="导出概览" />
      </header>
      <dl>
        <div><dt>阶段</dt><dd>{{ summary?.phaseCount ?? '—' }}</dd></div>
        <div><dt>任务</dt><dd>{{ summary?.taskCount ?? '—' }}</dd></div>
        <div><dt>计划节点</dt><dd>{{ summary?.plannedNodes ?? '—' }}</dd></div>
        <div><dt>完成节点</dt><dd>{{ summary?.completedNodes ?? '—' }}</dd></div>
        <div><dt>关键交付物</dt><dd>{{ summary?.keyDeliverables ?? '—' }}</dd></div>
        <div><dt>节点总数</dt><dd>{{ summary?.nodeCount ?? '—' }}</dd></div>
      </dl>
      <div class="project-meta">
        <span>项目周期</span>
        <b>
          {{ formatDate(project.startDate) }} — {{ formatDate(project.endDate) }}
          <!--
            结束日期取自 GATE6 时必须说出来（0824）。不说的话，
            人只会看到这个数字和建项目时填的对不上，第一反应是数据坏了；
            其实一行都没被改写，只是读的时候以计划里排的那个为准。
          -->
          <em v-if="project.endDateFromCloseGate" class="period-source">
            结束按「项目关闭」计划<template v-if="project.projectEndDate">
              （建项目时填的是 {{ formatDate(project.projectEndDate) }}）</template>
          </em>
        </b>
        <span>项目类型</span><b>{{ project.projectType || '未维护' }}</b>
      </div>
    </article>
    <article class="content-panel">
      <header class="section-heading"><div><span>MILESTONES &amp; GATES</span><h2>里程碑与 Gate</h2></div><RouterLink class="text-link" :to="`/projects/${project.id}/plan`">计划甘特 →</RouterLink></header>
      <template v-for="group in milestoneGroups" :key="group.audience">
      <p class="ms-group" :class="`ms-group-${group.audience}`">
        <b>{{ group.label }}</b><small>{{ group.hint }}</small><em>{{ group.items.length }} 个</em>
      </p>
      <ol class="milestone-list">
        <li v-for="milestone in group.items" :key="milestone.id" :class="{ saving: savingMilestoneId === milestone.id }">
          <b class="ms-code">
            {{ metaOf(milestone.milestoneCode).title || milestone.milestoneCode }}
            <span>{{ milestone.milestoneCode }}</span>
            <!-- 阶段出口门是另一个维度：GATE3 既是 P03 出口，又是客户认的 T0 -->
            <i v-if="metaOf(milestone.milestoneCode).isGate" class="ms-gate">阶段出口</i>
          </b>
          <div v-if="canWrite" class="milestone-edit">
            <input
              class="ms-remark" type="text" :value="milestone.remark ?? ''" placeholder="说明"
              :aria-label="`${milestone.milestoneCode} 的说明`"
              @change="saveMilestone(milestone, 'remark', $event)"
            >
            <label>计划
              <input
                type="date" :value="(milestone.planDate ?? '').slice(0, 10)"
                :aria-label="`${milestone.milestoneCode} 的计划日期`"
                @change="saveMilestone(milestone, 'planDate', $event)"
              >
              <!--
                这个日期是系统跟着项目周期填的，不是人排的（0823 Tony 第 2 条）。
                必须标出来：一个自动来的日期长得和人排的一模一样，
                人会以为"已经有人排过了"而不再去确认。改一次就变成手填，标记消失。
              -->
              <em v-if="milestone.planDateSource === '项目结束日期'" class="ms-auto">
                跟项目结束日期
              </em>
            </label>
            <label>实际
              <!--
                实际日期记的是"事情已经发生"，选不到未来（0907 Tony：
                「还没完成就可以把时间填上去，这个是不合规的」）。
                后端也拦一道——浏览器的 max 只挡日期选择器，手打或改 DOM 绕得过去。
              -->
              <input
                type="date" :value="(milestone.actualDate ?? '').slice(0, 10)"
                :max="today"
                :aria-label="`${milestone.milestoneCode} 的实际日期`"
                @change="saveMilestone(milestone, 'actualDate', $event)"
              >
              <!--
                这个日期是跟着项目归档自动记的，不是有人评审通过了 GATE6。
                两件事都是事实，但不是同一件——不标出来，看的人会以为走过评审了。
              -->
              <em v-if="milestone.actualDateSource === '项目关闭'" class="ms-auto">
                跟项目关闭
              </em>
            </label>
          </div>
          <div v-else><span>{{ milestone.remark || '未提供说明' }}</span><time>{{ formatDate(milestone.planDate) }}</time></div>
          <StatusChip :value="milestone.gateStatus || (milestone.actualDate ? 'completed' : milestone.planDate ? 'pending' : 'warning')" :label="milestoneStatusLabel(milestone)" />
          <!--
            逾期了就在这一行下面说清楚有没有挽回计划（0923 按准则改项目灯）：
            没登记 = 项目灯按红算；登记了、挽回日期没过 = 按黄算。改上面的「计划」日期不算挽回。
          -->
          <MilestoneRecoveryForm
            class="ms-recovery-row"
            :project-id="project.id"
            :milestone="milestone"
            :today="today"
            :editable="canWrite"
            @saved="applyRecovery(milestone, $event)"
          />
        </li>
      </ol>
      </template>
      <ol v-if="!milestones.length" class="milestone-list">
        <li class="empty-row">该项目还没有里程碑。</li>
      </ol>
    </article>
  </section>
</template>

<style scoped>
.ms-auto { margin-left: 4px; color: var(--muted); font-size: 10px; font-style: normal; white-space: nowrap; }
.readiness-span { grid-column: 1 / -1; }
.ms-group { display: flex; align-items: baseline; gap: 9px; margin: 14px 0 4px; flex-wrap: wrap; }
.ms-group:first-of-type { margin-top: 4px; }
.ms-group b { font-size: 13px; }
.ms-group small { color: var(--muted); font-size: 12px; }
.ms-group em { margin-left: auto; color: var(--muted); font-style: normal; font-size: 12px; }
.ms-group-customer b { color: var(--blue); }
.ms-code { display: flex; align-items: baseline; gap: 7px; flex-wrap: wrap; }
.ms-code span { color: var(--muted); font-size: 11px; font-weight: 400; letter-spacing: .04em; }
.ms-code .ms-gate {
  padding: 1px 7px; border-radius: 999px; background: rgba(0, 117, 174, .1);
  color: var(--blue); font-style: normal; font-size: 11px; font-weight: 700;
}
.milestone-edit { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.milestone-edit .ms-remark { flex: 1; min-width: 160px; min-height: 34px; padding: 0 9px; border: 1px solid transparent; border-radius: var(--radius-s); background: transparent; font: inherit; font-size: 13px; }
.milestone-edit .ms-remark:hover, .milestone-edit .ms-remark:focus { border-color: var(--line); background: var(--white); }
.milestone-edit label { display: flex; align-items: center; gap: 5px; color: var(--muted); font-size: 12px; white-space: nowrap; }
.milestone-edit input[type=date] { min-height: 34px; padding: 0 6px; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); font: inherit; font-size: 12px; }
.milestone-list li.saving { opacity: .55; }
/* 挽回计划那一块占满整行，排在里程碑这一行的下面 */
.milestone-list li > .ms-recovery-row { grid-column: 1 / -1; margin-bottom: 8px; }
/* 结束日期的来源说明：跟在日期后面另起一行，别把「项目周期」那一行撑宽 */
.period-source { display: block; margin-top: 2px; color: var(--muted); font: 400 12px var(--sans, inherit); font-style: normal; }

/*
  手机项目概览横向溢出（1028 Soft-Gate P3，复评步骤 09：可视 380px、内容 386px，右侧卡片贴边）。
  不是工作区外壳的事：里程碑一行是「80px 编号 | 说明 + 计划 + 实际 | 状态」，说明框最少 160px、
  两个日期框不换行，这一行缩不到卡片内宽；概览又是单列 1fr（= minmax(auto, 1fr)），
  于是整列卡片被它撑过屏幕。窄屏改成两行（编号和状态在上，说明与日期占满下一行），
  单列轨道也不再跟着子项的最小宽度长。宽屏一条不动。
*/
@media (max-width: 1280px) {
  .workspace-grid { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 600px) {
  .milestone-list li { grid-template-columns: minmax(0, 1fr) auto; }
  .milestone-list li > .ms-code + div { grid-column: 1 / -1; grid-row: 2; }
  .milestone-edit .ms-remark { flex-basis: 100%; min-width: 0; }
}
</style>
